import { NoteViewer } from "@/components/note/viewer/NoteViewer";
import { SpringAnimator } from "@/design-system/components/animation";
import { HoveringState } from "@/design-system/components/item-list/rows/item-preview/HoveringState";
import { ZIndex } from "@/domains/design/constants";
import { css, keyframes } from "@/domains/emotion";
import { INoteObservable } from "@/store/note/types";
import { useAppStore } from "@/store/utils/hooks";
import { MDSNotePreviewContainer } from "@/mds/components/note-preview-container";
import {
  FloatingNode,
  FloatingPortal,
  Placement,
  flip,
  offset,
  safePolygon,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { AnimatePresence } from "motion/react";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { mdsSpacings } from "@/design-system/foundations/typography";

export interface FloatingNotePreviewCardProps {
  state: HoveringState;
  noteObservable?: INoteObservable;
  placement?: Placement;
  highlightText?: string;
}

export const FloatingNotePreview = observer<FloatingNotePreviewCardProps>(
  ({ noteObservable, state, placement, highlightText }) => {
    const { store } = useAppStore();
    const nodeId = useFloatingNodeId();

    const { x, y, refs, strategy, context } = useFloating({
      nodeId,
      strategy: "fixed",
      middleware: [flip(), offset({ mainAxis: 18 })],
      open: true,
      onOpenChange: value => {
        if (value) {
          /** We don't want to open on hover states. */
          return;
        }
      },
      placement: placement ?? "bottom-end",
    });

    useEffect(() => {
      if (state.hoveredRect) {
        refs.setPositionReference({
          getBoundingClientRect() {
            return state.hoveredRect!;
          },
        });
      }
    }, [refs, state.hoveredRect]);

    const { getReferenceProps, getFloatingProps } = useInteractions([
      useHover(context, {
        move: false,
        handleClose: safePolygon(),
        delay: {
          close: 100,
        },
      }),
      useDismiss(context),
    ]);

    const portalContent = useMemo(() => {
      if (!noteObservable) {
        return false;
      }

      return (
        <SpringAnimator
          className={animationStyles}
          onClick={e => e.stopPropagation()}
          ref={refs.setFloating}
          style={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
            zIndex: ZIndex.FloatingNotePreview,
          }}
          {...getFloatingProps()}
          onMouseLeave={state.handleMouseLeave}
          onMouseEnter={state.handleMouseEnter}
        >
          <MDSNotePreviewContainer
            title={noteObservable.title}
            createdAt={noteObservable.locallyCreatedAt}
            modifiedAt={noteObservable.modifiedAt}
            authorNames={noteObservable.authors.map(author => author.profileDisplayName)}
            updateOnly={true}
          >
            <NoteViewer
              className={editorStyles}
              noteObservable={noteObservable}
              goToMention={store.navigation.goToMention}
              showMetadata={false}
              highlightText={highlightText}
            />
          </MDSNotePreviewContainer>
        </SpringAnimator>
      );
    }, [
      getFloatingProps,
      highlightText,
      noteObservable,
      refs.setFloating,
      state.handleMouseEnter,
      state.handleMouseLeave,
      store,
      strategy,
      x,
      y,
    ]);

    return (
      <FloatingNode id={nodeId}>
        <div ref={refs.setReference} {...getReferenceProps()}></div>
        <FloatingPortal>
          <AnimatePresence>{portalContent}</AnimatePresence>
        </FloatingPortal>
      </FloatingNode>
    );
  }
);

const growAndFadeAnimation = keyframes({
  from: {
    opacity: 0,
    transform: "scale(0.97)",
  },
  to: {
    opacity: 1,
    transform: "scale(1)",
  },
});

const animationStyles = css({
  width: "400px",
  animation: `${growAndFadeAnimation} .2s ease-out`,
});

const editorStyles = css({
  height: "250px", // Fixed height for the editor area
  overflowY: "auto",
  scrollbarWidth: "none",
  padding: `${mdsSpacings().md} ${mdsSpacings().md} 0 ${mdsSpacings().md}`,
  "::-webkit-scrollbar": {
    display: "none",
  },
});
